import { useState, useCallback } from "react";
import { formatDate } from "../../../../utils/date";

import useProfileData from "./useProfileData";
import useProfileForm from "./useProfileForm";
import useProfileNavigation from "./useProfileNavigation";
import useProfileModals from "./useProfileModals";
import useEnrollments from "./useEnrollments";
import useMemberMemberships from "./useMemberMemberships";
import useMemberFinancials from "./useMemberFinancials";

import getMembershipColumns from "../components/Columns/MembershipColumns";
import getEnrollmentColumns from "../components/Columns/EnrollmentColumns";
import { getFinancialColumns } from "../components/Columns/FinancialColumns";

/**
 * useProfile
 * 
 * Orquestrador do módulo MemberProfile.
 * Compõe todos os hooks especializados e monta o retorno
 * estruturado para consumo direto pelo index.js.
 * 
 * NÃO contém lógica de negócio própria — apenas composição.
 */
const useProfile = () => {

    // ─── Hooks especializados ───
    const profileData = useProfileData();
    const { member, setMember, idTenant, user, fetchMember } = profileData;

    const profileForm = useProfileForm(member, setMember, idTenant, user);
    const navigation = useProfileNavigation(member);
    const modals = useProfileModals(idTenant);

    // ─── Sub-hooks de domínio ───
    const enrollmentsHook = useEnrollments(member);
    const membershipsHook = useMemberMemberships(member);
    const financialsHook = useMemberFinancials(member);

    // ─── Tabs ───
    const [activeTab, setActiveTab] = useState("1");

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    /** Ao clicar em editar, sempre volta para a aba "Meus Dados" */
    const handleEdit = () => {
        profileForm.handleEdit();
        setActiveTab("1");
    };

    // ─── Refresh combinado de planos ───
    const { refresh: refreshMemberships } = membershipsHook;
    const handleMembershipRefresh = useCallback(async () => {
        await refreshMemberships();
        await new Promise(resolve => setTimeout(resolve, 2000));
        await fetchMember(false);
    }, [fetchMember, refreshMemberships]);

    // ─── Colunas de tabelas (montadas com callbacks dos modais) ───
    const enrollmentColumns = getEnrollmentColumns({
        formatDate,
        canceling: enrollmentsHook.canceling,
        onCancel: enrollmentsHook.handleCancelEnrollment,
    });

    const membershipColumns = getMembershipColumns({
        formatDate,
        onViewDetails: modals.handleViewMembershipDetails,
        onPrintReceipt: modals.handlePrintReceipt,
    });

    const financialColumns = getFinancialColumns({
        formatDate,
        onViewSale: (row) => modals.handlePrintReceipt({ idSale: row.idSale }),
    });

    // ─── Retorno estruturado ───
    return {
        // Estado geral
        loading: profileData.loading,
        error: profileData.error,
        member,

        // Edição
        formik: profileForm.formik,
        isEditing: profileForm.isEditing,
        handleEdit,
        handleSave: profileForm.handleSave,
        handleCancelEdit: profileForm.handleCancelEdit,
        handlePhotoChange: profileForm.handlePhotoChange,

        // Tabs
        activeTab,
        toggleTab,

        // Navegação
        handleNewSale: navigation.handleNewSale,
        handlePayDebt: navigation.handlePayDebt,
        handleNewEnrollment: navigation.handleNewEnrollment,

        // Matrículas
        enrollments: enrollmentsHook.enrollments,
        enrollmentsLoading: enrollmentsHook.loading,
        enrollmentColumns,

        // Planos
        memberMemberships: membershipsHook.memberships,
        membershipsLoading: membershipsHook.loading,
        membershipColumns,

        // Financeiro
        financials: financialsHook.financials,
        financialsLoading: financialsHook.loading,
        financialColumns,
        totalDebt: financialsHook.totalDebt,

        // Modal: Ações do Plano
        isActionsModalOpen: modals.isActionsModalOpen,
        toggleActionsModal: modals.toggleActionsModal,
        selectedMembership: modals.selectedMembership,
        handleMembershipRefresh,

        // Modal: Recibo
        receiptModalOpen: modals.receiptModalOpen,
        toggleReceiptModal: modals.toggleReceiptModal,
        currentReceiptData: modals.currentReceiptData,
    };
};

export default useProfile;
